import { useCallback, useEffect, useRef, useState } from 'react';
import type { Field } from '@formily/core';
import { useField } from '@formily/react';
import { observer } from '@formily/reactive-react';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import Epi from '../../../components/Epi/Epi';
import type { EpiDocumentData } from '../../../components/Epi/types/types';
import { normalizeForSave } from '../../../components/Epi/utils/epiHelpers';
import type { IComponentProps } from './index';
import { toEpiProps } from './toEpiProps';

/**
 * The field stays empty until the first edit and then holds the whole
 * document, save-ready. Empty means "the instance, unchanged", so opening the
 * page is not an edit and a draft only ever records real ones. The Form's
 * initial values are never written: Formily reacts to those by patching the
 * values, which loops.
 */
const EpiForFormField = observer(() => {
  const field = useField<Field>();
  const componentProps = (field.componentProps || {}) as IComponentProps;
  const instance = componentProps.epiInstance;
  const edited = isEmpty(field.value)
    ? undefined
    : (cloneDeep(field.value) as EpiDocumentData);

  // What the editor last wrote, so a value it did not write — a loaded or
  // discarded draft — is told apart and adopted.
  const emitted = useRef<EpiDocumentData | undefined>(undefined);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (isEqual(edited, emitted.current)) return;
    emitted.current = edited;
    setRevision(r => r + 1);
  }, [edited]);

  // A save refetches the instance, and the edits are in it now.
  const syncedVersion = useRef(instance?.version);
  useEffect(() => {
    if (instance?.version === syncedVersion.current) return;
    syncedVersion.current = instance?.version;
    emitted.current = undefined;
    if (!isEmpty(field.value)) field.setValue({});
    setRevision(r => r + 1);
  }, [instance?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  // The editor reports the whole document after every change, re-seeding
  // included; only a real difference reaches the field.
  const handleChange = useCallback(
    (data: EpiDocumentData) => {
      const current =
        emitted.current ??
        (instance?.data ? normalizeForSave(instance.data) : undefined);
      if (isEqual(data, current)) return;
      emitted.current = data;
      field.onInput(data);
    },
    [field, instance?.data]
  );

  if (componentProps.visible === false) return null;

  return (
    <Epi
      {...toEpiProps(componentProps)}
      data={edited}
      syncKey={revision}
      onChange={handleChange}
    />
  );
});

export default EpiForFormField;
